import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GeolocationStatus, IconButton, useGeolocation, useZoom } from '@agissc/ktag-map';
import useAppState from '../../AppStore';
import { FlyinNames } from '../../UIStateSlice';
import { useTracking } from '../../utils/tracking/TrackingContext';

const Navigation = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const location = useAppState((state) => state.location);
  const setLocation = useAppState((state) => state.setLocation);
  const geolocationStatus = useAppState((state) => state.geolocationStatus);
  const activeFlyin = useAppState((state) => state.activeFlyin);
  const setActiveFlyin = useAppState((state) => state.setActiveFlyin);
  const viewCollapse = useAppState((state) => state.viewCollapse);
  const navigationIsEnabled = useAppState((state) => state.navigationIsEnabled);
  const isLoggedIn = useAppState((state) => state.isLoggedIn);

  const onGeolocationRequest = async () => {
    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
    Tracking.Geolocation.permissionState(permissionStatus.state);
  };

  const onGeoLocationChange = () => Tracking.Geolocation.toggle(GeolocationStatus.Enable);
  const onToggleGeolocation = () => Tracking.Geolocation.toggle(geolocationStatus);

  const { handleGeolocation, isGeolocationEnabled } = useGeolocation({
    onGeolocationRequest,
    onGeoLocationChange,
    onToggleGeolocation,
  });

  const { zoomIn, zoomOut, zoomReset, canZoomIn, canZoomOut } = useZoom({
    minZoom: 0,
    maxZoom: isLoggedIn ? 14 : 13,
  });

  const handleZoomInClicked = () => {
    Tracking.Navigation.zoomIn('Navigation');
    zoomIn();
  };

  const handleZoomOutClicked = () => {
    Tracking.Navigation.zoomOut('Navigation');
    zoomOut();
  };

  const handleZoomReset = () => {
    Tracking.Navigation.zoomToCanton('Navigation');
    zoomReset();
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    const isTextInput =
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement;

    if (isTextInput) return;

    if (event.key === '+') {
      Tracking.Navigation.zoomIn('Keyboard');
      handleZoomInClicked();
    } else if (event.key === '-') {
      Tracking.Navigation.zoomOut('Keyboard');
      handleZoomOutClicked();
    }
  };

  const toggleLegend = () => {
    const legend = activeFlyin === FlyinNames.Legends ? null : FlyinNames.Legends;
    Tracking.Legend.toggle(legend ? 'open' : 'close');
    setActiveFlyin(legend);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  let iconTitle;
  if (geolocationStatus === GeolocationStatus.Denied) {
    iconTitle = t('geolocation_blocked');
  } else if (isGeolocationEnabled) {
    iconTitle = t('geolocation_active');
  } else {
    iconTitle = t('geolocation_inactive');
  }

  const isLegendsEnable = activeFlyin === FlyinNames.Legends;

  const isMapRotated = (() => {
    if (location.rotation === undefined) {
      return false;
    }

    const degrees = location.rotation * (180 / Math.PI);
    const parsedValue = parseFloat(degrees.toFixed(2));
    return parsedValue !== 0;
  })();

  return (
    <div
      className="navigation__container"
      title={navigationIsEnabled ? '' : t('print_navigation_notification')}
    >
      {isMapRotated && (
        <div className="navigation__item">
          <IconButton
            icon="north-up"
            title={t('reset_north')}
            additionalClassNames="icon-button"
            onClick={() => setLocation({ ...location, rotation: 0 })}
          />
        </div>
      )}
      <div className="navigation__item navigation__item-without-bottom-border">
        <IconButton
          icon="geopointer"
          title={navigationIsEnabled ? iconTitle : t('print_navigation_notification')}
          additionalClassNames="icon-button"
          isActive={isGeolocationEnabled}
          onClick={handleGeolocation}
          disabled={
            geolocationStatus === GeolocationStatus.Denied || !navigationIsEnabled
          }
        />
      </div>
      <div className="navigation__item">
        <IconButton
          icon="ag-map"
          onClick={handleZoomReset}
          title={
            navigationIsEnabled ? t('zoom_reset') : t('print_navigation_notification')
          }
          additionalClassNames="icon-button"
          disabled={!navigationIsEnabled}
        />
      </div>

      <div className="navigation__item-sparator" />

      <div className="navigation__item navigation__item-without-bottom-border navigation__item-zoom">
        <IconButton
          icon="zoom-in"
          title={navigationIsEnabled ? t('zoom_in') : t('print_navigation_notification')}
          onClick={handleZoomInClicked}
          disabled={!canZoomIn || !navigationIsEnabled}
        />
      </div>
      <div className="navigation__item navigation__item-zoom">
        <IconButton
          icon="zoom-out"
          title={navigationIsEnabled ? t('zoom_out') : t('print_navigation_notification')}
          onClick={handleZoomOutClicked}
          disabled={!canZoomOut || !navigationIsEnabled}
        />
      </div>

      <div className="navigation__item-sparator" />

      <div className="navigation__item">
        <IconButton
          icon="processes"
          title={isLegendsEnable ? t('hide_legend') : t('show_legend')}
          additionalClassNames="icon-button"
          isActive={isLegendsEnable && !viewCollapse}
          onClick={toggleLegend}
          disabled={viewCollapse}
        />
      </div>
    </div>
  );
};

export default Navigation;
